import { getStoredToken } from "./authStorage";
import type { SellerReceiptContext, SellerReceiptCreationAuditItem } from './types/orders';

// API Gateway для проксирования запросов к внутренним сервисам
export const API_GATEWAY_URL = import.meta.env.VITE_API_GATEWAY_URL || '/api';

export class ApiGatewayError extends Error {
    status: number;
    errorId?: string;
    payload?: unknown;

    constructor(message: string, status: number, errorId?: string, payload?: unknown) {
        super(message);
        this.name = 'ApiGatewayError';
        this.status = status;
        this.errorId = errorId;
        this.payload = payload;
    }
}

// Функция для создания прокси-запроса
async function proxyRequest(endpoint: string, options: RequestInit = {}) {
    const url = `${API_GATEWAY_URL}${endpoint}`;
    const token = getStoredToken();
    
    const response = await fetch(url, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...(options.headers || {}),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
    });

    if (!response.ok) {
        let errorPayload: unknown = null;
        const errorText = await response.text();
        if (errorText) {
            try {
                errorPayload = JSON.parse(errorText);
            } catch {
                errorPayload = errorText;
            }
        }
        const serviceError = errorPayload && typeof errorPayload === 'object'
            ? (errorPayload as { error?: { id?: string; message?: string } | string }).error
            : null;
        const errorId = serviceError && typeof serviceError === 'object' ? serviceError.id : undefined;
        const serviceMessage = typeof serviceError === 'string'
            ? serviceError
            : serviceError?.message;
        throw new ApiGatewayError(
            `API Error: ${response.status} ${response.statusText}${serviceMessage ? ` | ${serviceMessage}` : ''}`,
            response.status,
            errorId,
            errorPayload
        );
    }

    return response;
}

export type Platform = 'ANDROID' | 'IOS' | 'WEB';

export interface RemoteConfigRule {
    id: string;
    key: string;
    value: unknown;
    platform: Platform | null;
    minAppVersion: string | null;
    maxAppVersion: string | null;
    priority: number;
    enabled: boolean;
    description: string | null;
}

export interface CreateRemoteConfigRuleRequest {
    key: string;
    value: unknown;
    platform?: Platform | null;
    minAppVersion?: string | null;
    maxAppVersion?: string | null;
    priority?: number;
    enabled?: boolean;
    description?: string | null;
}

export interface PatchRemoteConfigRuleRequest {
    value?: unknown;
    platform?: Platform;
    minAppVersion?: string;
    maxAppVersion?: string;
    priority?: number;
    enabled?: boolean;
    description?: string;
}

export type RemoteConfigAuditAction = 'CREATED' | 'DELETED' | 'ENABLED' | 'DISABLED' | 'UPDATED';

export interface RemoteConfigAuditValueState {
    exists: boolean;
    value?: unknown;
}

export interface RemoteConfigAuditChange {
    path: string;
    before: RemoteConfigAuditValueState;
    after: RemoteConfigAuditValueState;
}

export interface RemoteConfigAuditItem {
    id: string;
    action: RemoteConfigAuditAction;
    ruleId: string;
    ruleKey: string;
    actor: {
        userId: string;
        login: string;
        fullName: string;
        role: string;
    };
    createdAt: string | null;
    beforeSnapshot: RemoteConfigRule | null;
    afterSnapshot: RemoteConfigRule | null;
    changes: RemoteConfigAuditChange[];
}

export interface RemoteConfigAuditPage {
    items: RemoteConfigAuditItem[];
    pagination: {
        limit: number;
        hasNextPage: boolean;
        nextCursor: string | null;
    };
}

type ServiceEnvelope<T> = {
    success: boolean;
    body?: T;
};

async function readEnvelopeBody<T>(response: Response): Promise<T> {
    const payload = await response.json() as ServiceEnvelope<T>;
    return payload.body as T;
}

export const remoteConfigProxy = {
    getList: async (filters: { platform?: Platform; key?: string; enabled?: boolean }) => {
        const query = new URLSearchParams();
        if (filters.platform) {
            query.set('platform', filters.platform);
        }
        if (filters.key) {
            query.set('key', filters.key);
        }
        if (typeof filters.enabled === 'boolean') {
            query.set('enabled', String(filters.enabled));
        }
        const suffix = query.toString() ? `?${query.toString()}` : '';
        const response = await proxyRequest(`/remote-config${suffix}`);
        return readEnvelopeBody<RemoteConfigRule[]>(response);
    },
    getOne: async (id: string) => {
        const response = await proxyRequest(`/remote-config/${encodeURIComponent(id)}`);
        return readEnvelopeBody<RemoteConfigRule>(response);
    },
    create: async (data: CreateRemoteConfigRuleRequest) => {
        const response = await proxyRequest('/remote-config', {
            method: 'POST',
            body: JSON.stringify(data)
        });
        return readEnvelopeBody<RemoteConfigRule>(response);
    },
    update: async (id: string, data: PatchRemoteConfigRuleRequest) => {
        const response = await proxyRequest(`/remote-config/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify(data)
        });
        return readEnvelopeBody<RemoteConfigRule>(response);
    },
    delete: async (id: string) => {
        const response = await proxyRequest(`/remote-config/${encodeURIComponent(id)}`, {
            method: 'DELETE'
        });
        return response.json() as Promise<{ success: boolean }>;
    },
    resolve: async (params: { platform?: Platform; appVersion?: string }) => {
        const query = new URLSearchParams();
        if (params.platform) {
            query.set('platform', params.platform);
        }
        const suffix = query.toString() ? `?${query.toString()}` : '';
        const response = await proxyRequest(`/remote-config/resolve${suffix}`, {
            headers: params.appVersion ? { 'X-App-Version': params.appVersion } : undefined
        });
        return readEnvelopeBody<Record<string, unknown>>(response);
    },
    getAudit: async (params: { cursor?: string | null; limit?: number } = {}) => {
        const query = new URLSearchParams();
        if (params.cursor) {
            query.set('cursor', params.cursor);
        }
        if (params.limit) {
            query.set('limit', String(params.limit));
        }
        const suffix = query.toString() ? `?${query.toString()}` : '';
        const response = await proxyRequest(`/remote-config/audit${suffix}`);
        return response.json() as Promise<RemoteConfigAuditPage>;
    }
};

// Прокси для Reviews API
export const reviewsProxy = {
    getList: async (params: any) => {
        const { pagination, meta } = params;
        const perPage = pagination?.perPage || 10;
        const cursor = meta?.cursor;
        
        let url = `/reviews/moderation?limit=${perPage}`;
        if (cursor) {
            url += `&cursor=${encodeURIComponent(cursor)}`;
        }
        
        const response = await proxyRequest(url);
        return response.json();
    },
    
    getOne: async (id: string) => {
        const response = await proxyRequest(`/reviews/${id}`);
        return response.json();
    },
    
    updateStatus: async (id: string, data: any) => {
        const response = await proxyRequest(`/reviews/${id}`, {
            method: 'POST',
            body: JSON.stringify(data)
        });
        return response.json();
    }
};

// Прокси для Ads API
export const adsProxy = {
    getList: async (moderationType: string, params: any) => {
        const { pagination, meta } = params;
        const perPage = pagination?.perPage || 10;
        const cursor = meta?.cursor;
        
        let url = `/ads/moderation?moderationType=${moderationType}&limit=${perPage}`;
        if (cursor) {
            url += `&cursor=${encodeURIComponent(cursor)}`;
        }
        
        const response = await proxyRequest(url);
        return response.json();
    },
    
    getOne: async (id: string) => {
        const response = await proxyRequest(`/ads/ad/${id}`);
        return response.json();
    },

    getExpertOne: async (id: string) => {
        const response = await proxyRequest(`/ads/expert-ad/${id}`);
        return response.json();
    },
    
    updateStatus: async (
        id: string,
        data: any,
        options?: { moderationType?: 'FIRST_MODERATION' | 'EXPERT_MODERATION' }
    ) => {
        const response = await proxyRequest(`/ads/adStatus/${id}`, {
            method: 'POST',
            headers: options?.moderationType
                ? { 'X-Moderation-Type': options.moderationType }
                : undefined,
            body: JSON.stringify(data)
        });
        return response.json();
    },
    sendBackToModerators: async (id: string, comment: string) => {
        const response = await proxyRequest(`/ads/${encodeURIComponent(id)}/status/moderation`, {
            method: 'POST',
            body: JSON.stringify({ comment })
        });
        return response.json();
    },

    getExpertModerationReturns: async (id: string) => {
        const response = await proxyRequest(`/ads/${encodeURIComponent(id)}/expert-moderation-returns`);
        return response.json() as Promise<{ items: ExpertModerationReturn[] }>;
    },

    getAdConfig: async () => {
        const response = await proxyRequest(`/ads/adConfig`);
        return response.json();
    },

    getDuplicates: async (id: string) => {
        const response = await proxyRequest(`/ads/duplicates/${encodeURIComponent(id)}`);
        return response.json();
    }
};

export interface ExpertModerationReturn {
    id: string;
    adId: string;
    comment: string;
    expert: {
        userId: string;
        login: string;
        fullName: string;
        role: string;
    };
    createdAt: string | null;
}

export interface AiModerationConfig {
    enabled: boolean;
    rolloutPercent: number;
    batchSize: number;
    maxAttempts: number;
    delaySeconds: number;
    updatedAt: string;
}

export interface SetAiModerationConfigRequest {
    enabled?: boolean;
    rolloutPercent?: number;
    batchSize?: number;
    maxAttempts?: number;
    delaySeconds?: number;
}

export interface AiModerationConfigHistoryItem {
    id: string;
    createdAt: string | null;
    actor: {
        userId: string;
        login: string;
        fullName: string;
        role: string;
    };
    changedFields: Partial<Record<keyof SetAiModerationConfigRequest, {
        before: boolean | number | null;
        after: boolean | number | null;
    }>>;
}

export const aiModerationConfigProxy = {
    getConfig: async (): Promise<AiModerationConfig> => {
        const response = await proxyRequest('/ads/aiConfig');
        const data = await response.json();
        return data?.body ?? data;
    },
    updateConfig: async (data: SetAiModerationConfigRequest): Promise<AiModerationConfig> => {
        const response = await proxyRequest('/ads/aiConfig', {
            method: 'PATCH',
            body: JSON.stringify(data)
        });
        const responseData = await response.json();
        return responseData?.body ?? responseData;
    },
    getHistory: async (limit = 20): Promise<{ data: AiModerationConfigHistoryItem[] }> => {
        const response = await proxyRequest(`/ads/aiConfig/history?limit=${encodeURIComponent(String(limit))}`);
        return response.json();
    }
};

// Прокси для Cloud AI Agents API (через API Gateway)
export const aiProxy = {
    callAgent: async (message: string, signal?: AbortSignal) => {
        const response = await proxyRequest(`/cloud-ai/agents/call`, {
            method: 'POST',
            body: JSON.stringify({ message }),
            signal
        });
        return response.json();
    },
    callLegitAgent: async (messages: any[], signal?: AbortSignal) => {
        const response = await proxyRequest(`/cloud-ai/agents/legit/chat/completions`, {
            method: 'POST',
            body: JSON.stringify({ messages }),
            signal
        });
        return response.json();
    }
};

export const aiSummariesProxy = {
    getSummary: async (adId: string) => {
        const response = await proxyRequest(`/ai-summaries/${adId}`);
        return response.json();
    },
    saveSummary: async (adId: string, summary: string) => {
        const response = await proxyRequest(`/ai-summaries/${adId}`, {
            method: 'POST',
            body: JSON.stringify({ summary })
        });
        return response.json();
    }
};

export const legitAiSummariesProxy = {
    getSummary: async (adId: string) => {
        const response = await proxyRequest(`/legit-ai-summaries/${adId}`);
        return response.json();
    },
    saveSummary: async (adId: string, summary: string) => {
        const response = await proxyRequest(`/legit-ai-summaries/${adId}`, {
            method: 'POST',
            body: JSON.stringify({ summary })
        });
        return response.json();
    }
};

export const customReasonsProxy = {
    getList: async () => {
        const response = await proxyRequest(`/custom-reasons`);
        return response.json();
    },
    create: async (data: { label: string; value: string; group: 'ads' | 'expert' | 'reviews'; reasonType: 'refusal' | 'rework' }) => {
        const response = await proxyRequest(`/custom-reasons`, {
            method: 'POST',
            body: JSON.stringify(data)
        });
        return response.json();
    },
    update: async (id: string, data: { label: string; value: string; group?: 'ads' | 'expert' | 'reviews'; reasonType?: 'refusal' | 'rework' }) => {
        const response = await proxyRequest(`/custom-reasons/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
        return response.json();
    },
    delete: async (id: string) => {
        const response = await proxyRequest(`/custom-reasons/${id}`, {
            method: 'DELETE'
        });
        return response.json();
    }
};

export type PromoCodeResponseType = 'Percent' | 'FixSum' | 'percent' | 'fixSum' | string;

export interface PromoCodeDb {
    id: string;
    code: string;
    description: string;
    type: PromoCodeResponseType;
    amount: number;
    maxUsageCount: number | null;
    conditions: string[];
    isActive: boolean;
    isDeleted: boolean;
}

export interface PromoCodeRequest {
    type: 'percent' | 'fixSum';
    code: string;
    description: string;
    amountDiscount: number;
    maxUsageCount: number | null;
    condition: {
        minSum: number;
        maxDiscountAmount: number | null;
        startDate: string | null;
        endDate: string | null;
    };
}

export interface UpdatePromoCodeDeletedStateRequest {
    code: string;
    isDeleted: boolean;
}

export const promoCodesProxy = {
    getList: async (params: { page: number; size: number }) => {
        const query = new URLSearchParams();
        query.set('page', String(params.page));
        query.set('size', String(params.size));
        const response = await proxyRequest(`/promo-codes?${query.toString()}`);
        return response.json();
    },
    create: async (data: PromoCodeRequest) => {
        const response = await proxyRequest('/promo-codes', {
            method: 'PUT',
            body: JSON.stringify(data)
        });
        return response.json();
    },
    updateDeletedState: async (data: UpdatePromoCodeDeletedStateRequest) => {
        const response = await proxyRequest('/promo-codes', {
            method: 'PATCH',
            body: JSON.stringify(data)
        });
        const textPayload = await response.text();
        if (!textPayload) {
            return null;
        }
        try {
            return JSON.parse(textPayload);
        } catch {
            return textPayload;
        }
    }
};

export const expertModerationStatsProxy = {
    getStats: async (month?: string) => {
        const query = month ? `?month=${encodeURIComponent(month)}` : '';
        const response = await proxyRequest(`/expert-moderation-stats${query}`);
        return response.json();
    }
};

export const expertTariffsProxy = {
    getConfig: async () => {
        const response = await proxyRequest('/expert-tariffs');
        return response.json();
    },
    updateConfig: async (data: {
        categoryTariffs: Record<string, Record<string, number>>;
        subcategoryTariffs: Record<string, Record<string, number>>;
    }) => {
        const response = await proxyRequest('/expert-tariffs', {
            method: 'PUT',
            body: JSON.stringify(data)
        });
        return response.json();
    },
    getHistory: async (limit = 100) => {
        const response = await proxyRequest(`/expert-tariffs/history?limit=${encodeURIComponent(String(limit))}`);
        return response.json();
    },
    getHistoryRevision: async (id: string) => {
        const response = await proxyRequest(`/expert-tariffs/history/${encodeURIComponent(id)}`);
        return response.json();
    }
};

export const expertSlaProxy = {
    getConfig: async () => {
        const response = await proxyRequest('/expert-sla');
        return response.json();
    },
    updateConfig: async (data: {
        slaEnabled?: boolean;
        penaltyPercent?: number;
        categorySlaHours: Record<string, number>;
        subcategorySlaHours: Record<string, number>;
    }) => {
        const response = await proxyRequest('/expert-sla', {
            method: 'PUT',
            body: JSON.stringify(data)
        });
        return response.json();
    },
    getHistory: async (limit = 100) => {
        const response = await proxyRequest(`/expert-sla/history?limit=${encodeURIComponent(String(limit))}`);
        return response.json();
    },
    getHistoryRevision: async (id: string) => {
        const response = await proxyRequest(`/expert-sla/history/${encodeURIComponent(id)}`);
        return response.json();
    }
};

export const expertPersonalStatsProxy = {
    getStats: async (month?: string) => {
        const query = month ? `?month=${encodeURIComponent(month)}` : '';
        const response = await proxyRequest(`/expert-personal-stats${query}`);
        return response.json();
    }
};

export const moderatorActionsStatsProxy = {
    getPersonalStats: async (params: {
        month?: string;
        dateFrom?: string;
        dateTo?: string;
        cursor?: string;
        limit?: number;
    }) => {
        const query = new URLSearchParams();
        if (params.month) {
            query.set('month', params.month);
        }
        if (params.dateFrom) {
            query.set('dateFrom', params.dateFrom);
        }
        if (params.dateTo) {
            query.set('dateTo', params.dateTo);
        }
        if (params.cursor) {
            query.set('cursor', params.cursor);
        }
        if (typeof params.limit === 'number' && Number.isFinite(params.limit)) {
            query.set('limit', String(Math.trunc(params.limit)));
        }

        const response = await proxyRequest(`/moderator-actions/personal?${query.toString()}`);
        return response.json();
    },
    getModerators: async () => {
        const response = await proxyRequest('/moderator-actions/moderators');
        return response.json();
    },
    getModeratorStats: async (
        moderatorId: string,
        params: {
            month?: string;
            dateFrom?: string;
            dateTo?: string;
            cursor?: string;
            limit?: number;
        }
    ) => {
        const query = new URLSearchParams();
        if (params.month) {
            query.set('month', params.month);
        }
        if (params.dateFrom) {
            query.set('dateFrom', params.dateFrom);
        }
        if (params.dateTo) {
            query.set('dateTo', params.dateTo);
        }
        if (params.cursor) {
            query.set('cursor', params.cursor);
        }
        if (typeof params.limit === 'number' && Number.isFinite(params.limit)) {
            query.set('limit', String(Math.trunc(params.limit)));
        }

        const response = await proxyRequest(`/moderator-actions/moderators/${encodeURIComponent(moderatorId)}?${query.toString()}`);
        return response.json();
    }
};

export type SellerDeficitInternalResponse = {
    sellerDeficitAmount: string;
};

export const paymentsProxy = {
    getSellersDeficit: async () => {
        const response = await proxyRequest('/payments/sellers/deficit');
        const data = await response.json();
        return (data?.body ?? data) as SellerDeficitInternalResponse;
    }
};

// Прокси для Complaints API
export const complaintsProxy = {
    getList: async (params: any) => {
        const { pagination, meta, filter } = params;
        const perPage = pagination?.perPage || 30;
        const cursor = meta?.cursor;
        const statusFilter = filter?.complaintStatusFilter;
        
        let url = `/complaints?limit=${perPage}`;
        if (cursor) {
            url += `&cursor=${encodeURIComponent(cursor)}`;
        }
        if (statusFilter && statusFilter !== 'ALL') {
            url += `&complaintStatusFilter=${encodeURIComponent(statusFilter)}`;
        }
        
        const response = await proxyRequest(url);
        return response.json();
    },
    
    getOne: async (id: string) => {
        const response = await proxyRequest(`/complaints/${id}`);
        return response.json();
    },
    
    updateStatus: async (id: string, data: any) => {
        const response = await proxyRequest(`/complaints/${id}/status`, {
            method: 'POST',
            body: JSON.stringify(data)
        });
        return response.json();
    }
};

// Прокси для Orders API
export const ordersProxy = {
    getOne: async (id: string | number) => {
        // Определяем тип поиска: если это число, то это номер заказа, иначе - идентификатор
        const isSerialNumber = !isNaN(Number(id)) && Number(id) > 0;
        
        let url: string;
        if (isSerialNumber) {
            url = `/orders/orderInfo?serialNumber=${id}`;
        } else {
            url = `/orders/orderInfo?id=${id}`;
        }
        
        const response = await proxyRequest(url);
        return response.json();
    },

    getPaymentInfo: async (orderId: string) => {
        const response = await proxyRequest(`/orders/orders/${encodeURIComponent(orderId)}/paymentInfo`);
        return response.json();
    },

    getReceiptContext: async (orderId: string) => {
        const response = await proxyRequest(`/orders/orders/${encodeURIComponent(orderId)}/receipt`);
        const data = await response.json();
        return (data?.body ?? data) as SellerReceiptContext;
    },

    createReceipt: async (orderId: string) => {
        const response = await proxyRequest(`/orders/orders/${encodeURIComponent(orderId)}/receipt`, {
            method: 'POST'
        });
        const responseText = await response.text();
        let data: unknown = null;
        if (responseText) {
            try {
                data = JSON.parse(responseText);
            } catch {
                data = responseText;
            }
        }
        return { status: response.status, data };
    },

    getReceiptAudit: async (orderId: string, limit = 50) => {
        const response = await proxyRequest(
            `/orders/orders/${encodeURIComponent(orderId)}/receipt/audit?limit=${encodeURIComponent(String(limit))}`
        );
        return response.json() as Promise<{ items: SellerReceiptCreationAuditItem[] }>;
    },
    
    updateStatus: async (id: string, data: any) => {
        const response = await proxyRequest(`/orders/orders/${id}/status`, {
            method: 'POST',
            body: JSON.stringify(data)
        });
        return response.json();
    },

    sendDeliveryEvent: async (id: string, data: any) => {
        const response = await proxyRequest(`/orders/orders/${id}/delivery/event`, {
            method: 'POST',
            body: JSON.stringify(data)
        });
        return response.json();
    }
};

export type UserAvatarDeletionHistoryItem = {
    id: string;
    userId: string;
    avatarId: string;
    deletedAt: string | null;
    moderator: { id: string; name: string; login: string; role: string };
};

export const usersProxy = {
    getAvatarDeletionHistory: async (userId: string): Promise<{ items: UserAvatarDeletionHistoryItem[] }> => {
        const searchParams = new URLSearchParams({ userId, limit: '100' });
        const response = await proxyRequest(`/users/avatarDeletionHistory?${searchParams.toString()}`);
        return response.json();
    },
    deleteAvatar: async (params: { userId: string; avatarId: string; confirmation?: string }) => {
        const response = await proxyRequest('/users/deleteUserAvatar', {
            method: 'POST',
            ...(params.confirmation ? { headers: { 'X-Avatar-Deletion-Confirmation': params.confirmation } } : {}),
            body: JSON.stringify({ userId: params.userId, avatarId: params.avatarId })
        });
        const data = await response.json();
        if (data?.success !== true) {
            throw new ApiGatewayError(
                data?.error?.message || 'Не удалось удалить фото',
                response.status,
                data?.error?.id,
                data
            );
        }
        return data;
    },
    search: async (params: { nickname?: string; phone?: string; id?: string }) => {
        const searchParams = new URLSearchParams();
        if (params.nickname) {
            searchParams.set('nickname', params.nickname);
        }
        if (params.phone) {
            searchParams.set('phone', params.phone);
        }
        if (params.id) {
            searchParams.set('id', params.id);
        }

        const query = searchParams.toString();
        const response = await proxyRequest(`/users/searchUser${query ? `?${query}` : ''}`);
        const data = await response.json();
        return data?.body ?? data;
    },
    getUserAds: async (params: { userId: string; cursor?: string | null }) => {
        const searchParams = new URLSearchParams();
        searchParams.set('userId', params.userId);
        if (params.cursor) {
            searchParams.set('cursor', params.cursor);
        }

        const query = searchParams.toString();
        const response = await proxyRequest(`/users/userAds${query ? `?${query}` : ''}`);
        const data = await response.json();
        return data?.body ?? data;
    },
    getUserAdsStatusCount: async (params: { userId: string }) => {
        const searchParams = new URLSearchParams();
        searchParams.set('userId', params.userId);

        const response = await proxyRequest(`/users/userAds/statusCount?${searchParams.toString()}`);
        const data = await response.json();
        return data?.body ?? data;
    },
    getUserFeeInfo: async (userId: string) => {
        const response = await proxyRequest(`/users/${encodeURIComponent(userId)}/feeInfo`);
        const data = await response.json();
        return data?.body ?? data;
    },
    setUserFeeType: async (
        userId: string,
        data:
            | { feeType: 'WELCOME_BONUS'; feePercent: number; untilDate: string }
            | { feeType: 'FIXED'; feePercent: number; untilDate?: string | null }
            | { feeType: 'DYNAMIC' }
    ) => {
        const response = await proxyRequest(`/users/${encodeURIComponent(userId)}/feeType`, {
            method: 'POST',
            body: JSON.stringify(data)
        });
        const responseText = await response.text();
        if (!responseText) {
            return null;
        }

        try {
            const responseData = JSON.parse(responseText);
            return responseData?.body ?? responseData;
        } catch {
            return responseText;
        }
    },
    changeBlockUser: async (data: { userId: string; isBlocked: boolean; comment: string }) => {
        const response = await proxyRequest('/users/changeBlockUser', {
            method: 'POST',
            body: JSON.stringify(data)
        });
        const responseText = await response.text();
        if (!responseText) {
            return null;
        }

        try {
            const responseData = JSON.parse(responseText);
            return responseData?.body ?? responseData;
        } catch {
            return responseText;
        }
    },
    replaceSpamMessages: async (userId: string) => {
        const response = await proxyRequest(`/users/${encodeURIComponent(userId)}/messages/replace-spam`, {
            method: 'POST'
        });
        const responseText = await response.text();
        if (!responseText) {
            return null;
        }

        try {
            const responseData = JSON.parse(responseText);
            return responseData?.body ?? responseData;
        } catch {
            return responseText;
        }
    },
    getUserBlockHistory: async (params: { userId: string; limit?: number }) => {
        const searchParams = new URLSearchParams();
        searchParams.set('userId', params.userId);
        if (typeof params.limit === 'number' && Number.isFinite(params.limit)) {
            searchParams.set('limit', String(Math.trunc(params.limit)));
        }

        const response = await proxyRequest(`/users/blockHistory?${searchParams.toString()}`);
        const data = await response.json();
        return data?.body ?? data;
    },
    getLatestUserOrders: async (params: {
        userId: string;
        type: 'buying' | 'selling';
        lastOrderSerialNumber?: string | number | null;
        limit?: number;
    }) => {
        const searchParams = new URLSearchParams();
        searchParams.set('type', params.type);
        const limit = typeof params.limit === 'number' && Number.isFinite(params.limit)
            ? Math.trunc(params.limit)
            : 30;
        searchParams.set('limit', String(limit));
        if (params.lastOrderSerialNumber !== undefined && params.lastOrderSerialNumber !== null) {
            const serializedCursor = String(params.lastOrderSerialNumber).trim();
            if (serializedCursor) {
                searchParams.set('lastOrderSerialNumber', serializedCursor);
            }
        }

        const response = await proxyRequest(
            `/users/${encodeURIComponent(params.userId)}/orders/latest?${searchParams.toString()}`
        );
        const data = await response.json();
        return data?.body ?? data;
    },
    getUserReviews: async (params: {
        userId: string;
        type: 'TO_USER' | 'FROM_USER';
        limit?: number;
        cursor?: string | null;
    }) => {
        const searchParams = new URLSearchParams();
        searchParams.set('type', params.type);
        const limit = typeof params.limit === 'number' && Number.isFinite(params.limit)
            ? Math.trunc(params.limit)
            : 50;
        searchParams.set('limit', String(limit));
        if (params.cursor) {
            searchParams.set('cursor', params.cursor);
        }

        const response = await proxyRequest(
            `/users/${encodeURIComponent(params.userId)}/reviews?${searchParams.toString()}`
        );
        const data = await response.json();
        return data?.body ?? data;
    },
    getUserPayouts: async (params: {
        userId: string;
        limit?: number;
        cursor?: string | null;
    }) => {
        const searchParams = new URLSearchParams();
        const limit = typeof params.limit === 'number' && Number.isFinite(params.limit)
            ? Math.trunc(params.limit)
            : 30;
        searchParams.set('limit', String(limit));
        if (params.cursor) {
            searchParams.set('cursor', params.cursor);
        }

        const response = await proxyRequest(
            `/users/${encodeURIComponent(params.userId)}/payouts?${searchParams.toString()}`
        );
        const data = await response.json();
        return data?.body ?? data;
    },
    getUserLinkedPaymentMethods: async (params: { userId: string }) => {
        const response = await proxyRequest(
            `/users/${encodeURIComponent(params.userId)}/methods`
        );
        const data = await response.json();
        return data?.body ?? data;
    },
    getUserBonuses: async (userId: string) => {
        const response = await proxyRequest(
            `/users/${encodeURIComponent(userId)}/bonuses`
        );
        const data = await response.json();
        return data?.body ?? data;
    },
    getUserBonusesHistory: async (params: { userId: string; limit?: number; cursor?: string | null }) => {
        const searchParams = new URLSearchParams();
        const limit = typeof params.limit === 'number' && Number.isFinite(params.limit)
            ? Math.trunc(params.limit)
            : 30;
        searchParams.set('limit', String(limit));
        if (params.cursor) {
            searchParams.set('cursor', params.cursor);
        }

        const response = await proxyRequest(
            `/users/${encodeURIComponent(params.userId)}/bonuses/history?${searchParams.toString()}`
        );
        const data = await response.json();
        return data?.body ?? data;
    },
    changeUserBonuses: async (
        userId: string,
        data: { operationType: 'ACCRUAL' | 'WRITE_OFF'; bonuses: number }
    ) => {
        const response = await proxyRequest(
            `/users/${encodeURIComponent(userId)}/bonuses`,
            {
                method: 'POST',
                body: JSON.stringify(data)
            }
        );
        const responseData = await response.json();
        const normalizedData = responseData?.body ?? responseData;
        return {
            success: normalizedData?.success === true
        };
    },
    setOrderPayoutBlocked: async (params: {
        orderId: string;
        blocked: boolean;
        reason: string;
        payoutId?: string;
    }) => {
        const response = await proxyRequest(
            `/users/orders/${encodeURIComponent(params.orderId)}/payout/block`,
            {
                method: 'POST',
                body: JSON.stringify({
                    blocked: params.blocked,
                    reason: params.reason,
                    payoutId: params.payoutId
                })
            }
        );
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
            const data = await response.json();
            return data?.body ?? data;
        }
        return response.text();
    },
    getUserPayoutBlockEvents: async (params: { payoutId: string; limit?: number }) => {
        const searchParams = new URLSearchParams();
        if (typeof params.limit === 'number' && Number.isFinite(params.limit)) {
            searchParams.set('limit', String(Math.trunc(params.limit)));
        }
        const query = searchParams.toString();

        const response = await proxyRequest(
            `/users/payouts/${encodeURIComponent(params.payoutId)}/block-events${query ? `?${query}` : ''}`
        );
        const data = await response.json();
        return data?.body ?? data;
    },
    getUserManualPayoutEvents: async (params: { payoutId: string; orderId?: string; ypmnId?: string; limit?: number }) => {
        const searchParams = new URLSearchParams();
        if (typeof params.limit === 'number' && Number.isFinite(params.limit)) {
            searchParams.set('limit', String(Math.trunc(params.limit)));
        }
        if (typeof params.orderId === 'string' && params.orderId.trim()) {
            searchParams.set('orderId', params.orderId.trim());
        }
        if (typeof params.ypmnId === 'string' && params.ypmnId.trim()) {
            searchParams.set('ypmnId', params.ypmnId.trim());
        }
        const query = searchParams.toString();

        const response = await proxyRequest(
            `/users/payouts/${encodeURIComponent(params.payoutId)}/manual-payout-events${query ? `?${query}` : ''}`
        );
        const data = await response.json();
        return data?.body ?? data;
    },
    manualPayout: async (params: { payoutId: string; orderId?: string; ypmnId?: string }) => {
        const response = await proxyRequest(
            `/users/payouts/${encodeURIComponent(params.payoutId)}/manual-payout`,
            {
                method: 'POST',
                body: JSON.stringify({
                    orderId: params.orderId,
                    ypmnId: params.ypmnId
                })
            }
        );
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
            const data = await response.json();
            return data?.body ?? data;
        }
        return response.text();
    },
    deleteUserReview: async (params: {
        reviewId: string;
        userId: string;
        reviewType: 'TO_USER' | 'FROM_USER';
        reason: string;
        reviewText?: string;
        reviewGradeValue?: number | null;
        orderId?: string;
    }) => {
        const response = await proxyRequest(
            `/users/reviews/${encodeURIComponent(params.reviewId)}/delete`,
            {
                method: 'DELETE',
                body: JSON.stringify({
                    userId: params.userId,
                    reviewType: params.reviewType,
                    reason: params.reason,
                    reviewText: params.reviewText,
                    reviewGradeValue: params.reviewGradeValue,
                    orderId: params.orderId
                })
            }
        );
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
            const data = await response.json();
            return data?.body ?? data;
        }
        return response.text();
    },
    deleteUserReviewReply: async (reviewId: string) => {
        const response = await proxyRequest(
            `/users/reviews/${encodeURIComponent(reviewId)}/reply/delete`,
            {
                method: 'DELETE'
            }
        );
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
            const data = await response.json();
            return data?.body ?? data;
        }
        return response.text();
    },
    getDeletedUserReviews: async (params: {
        userId: string;
        type?: 'TO_USER' | 'FROM_USER';
        limit?: number;
    }) => {
        const searchParams = new URLSearchParams();
        if (params.type) {
            searchParams.set('type', params.type);
        }
        if (typeof params.limit === 'number' && Number.isFinite(params.limit)) {
            searchParams.set('limit', String(Math.trunc(params.limit)));
        }

        const response = await proxyRequest(
            `/users/${encodeURIComponent(params.userId)}/reviews/deleted?${searchParams.toString()}`
        );
        const data = await response.json();
        return data?.body ?? data;
    },
    getUserReviewsStats: async (params: { userId: string }) => {
        const response = await proxyRequest(
            `/users/${encodeURIComponent(params.userId)}/reviews/stats`
        );
        const data = await response.json();
        return data?.body ?? data;
    },
    getAdInfo: async (identifier: string, options?: { source?: 'USER_AD_SHOW' }) => {
        const trimmedIdentifier = String(identifier).trim();
        const isSerialNumber = /^\d+$/.test(trimmedIdentifier);
        const searchParams = new URLSearchParams();
        if (isSerialNumber) {
            searchParams.set('serialNumber', trimmedIdentifier);
        } else {
            searchParams.set('id', trimmedIdentifier);
        }
        if (options?.source) {
            searchParams.set('source', options.source);
        }

        const response = await proxyRequest(`/users/adInfo?${searchParams.toString()}`);
        const data = await response.json();
        return data?.body ?? data;
    },
    changeAdBlockStatus: async (
        params:
            | { adId: string; action: 'BLOCK'; reason: string; internalComment: string }
            | { adId: string; action: 'UNBLOCK' }
    ) => {
        const { adId, ...requestBody } = params;
        const response = await proxyRequest(`/users/adBlock/${encodeURIComponent(adId)}`, {
            method: 'POST',
            body: JSON.stringify(requestBody)
        });
        const data = await response.json();
        return data?.body ?? data;
    },
    setAdStatus: async (
        params:
            | { adId: string; status: 'ACTIVE'; reason: string; hasOriginalLabel?: boolean }
            | { adId: string; status: 'MODERATION'; reason: string }
            | { adId: string; status: 'EXPERT_MODERATION'; reason: string }
    ) => {
        const { adId, ...requestBody } = params;
        const response = await proxyRequest(`/users/adStatus/${encodeURIComponent(adId)}`, {
            method: 'POST',
            body: JSON.stringify(requestBody)
        });
        const data = await response.json();
        return data?.body ?? data;
    },
    patchAdOtherPref: async (params: {
        adId: string;
        attachmentPhotoIds?: string[];
        brandName?: string;
        modelName?: string;
        otherPref?: {
            isExchange?: boolean;
            isVintage?: boolean;
            isExistBox?: boolean;
            serialNumber?: string;
        };
    }) => {
        const { adId, ...requestBody } = params;
        const response = await proxyRequest(`/users/adOtherPref/${encodeURIComponent(adId)}`, {
            method: 'PATCH',
            body: JSON.stringify(requestBody)
        });
        const responseText = await response.text();
        if (!responseText) {
            return null;
        }

        try {
            const responseData = JSON.parse(responseText);
            return responseData?.body ?? responseData;
        } catch {
            return responseText;
        }
    },
    getAdBlockEvents: async (params: { adId: string; limit?: number }) => {
        const searchParams = new URLSearchParams();
        searchParams.set('adId', params.adId);
        if (typeof params.limit === 'number' && Number.isFinite(params.limit)) {
            searchParams.set('limit', String(Math.trunc(params.limit)));
        }

        const response = await proxyRequest(`/users/adBlockEvents?${searchParams.toString()}`);
        const data = await response.json();
        return data?.body ?? data;
    },
    getAdStatusEvents: async (params: { adId: string; limit?: number }) => {
        const searchParams = new URLSearchParams();
        searchParams.set('adId', params.adId);
        if (typeof params.limit === 'number' && Number.isFinite(params.limit)) {
            searchParams.set('limit', String(Math.trunc(params.limit)));
        }

        const response = await proxyRequest(`/users/adStatusEvents?${searchParams.toString()}`);
        const data = await response.json();
        return data?.body ?? data;
    }
};

export const authProxy = {
    changePassword: async (data: { newPassword: string }) => {
        const response = await proxyRequest('/auth/change-password', {
            method: 'POST',
            body: JSON.stringify(data)
        });
        return response.json();
    }
};

export const adminUsersProxy = {
    getList: async () => {
        const response = await proxyRequest('/admin-users');
        return response.json();
    },
    getOne: async (id: string) => {
        const response = await proxyRequest(`/admin-users/${id}`);
        return response.json();
    },
    create: async (data: {
        login: string;
        fullName: string;
        role: 'manager-admin' | 'moderator' | 'moderator-admin' | 'expert' | 'expert-admin' | 'expert-outstaff' | 'expert-admin-outstaff' | 'developer';
        password: string;
        isActive: boolean;
    }) => {
        const response = await proxyRequest('/admin-users', {
            method: 'POST',
            body: JSON.stringify(data)
        });
        return response.json();
    },
    update: async (id: string, data: {
        login?: string;
        fullName?: string;
        role?: 'manager-admin' | 'moderator' | 'moderator-admin' | 'expert' | 'expert-admin' | 'expert-outstaff' | 'expert-admin-outstaff' | 'developer';
        password?: string;
        isActive?: boolean;
    }) => {
        const response = await proxyRequest(`/admin-users/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
        return response.json();
    },
    delete: async (id: string) => {
        const response = await proxyRequest(`/admin-users/${id}`, {
            method: 'DELETE'
        });
        return response.json();
    }
};
